// Shared constants for the rest timer. Both the web hook
// (apps/web/src/workout/restTimer.ts) and the mobile hook
// (apps/mobile/src/logger/useRestTimer.ts) import from here so the
// "+1 min" gesture and the catalog-less fallback agree across
// platforms. Sound / vibration / DOM timers stay per-app — only the
// pure values live here.

// "+1 min" gesture between heavy sets. Tapping it twice gives two
// minutes, etc. One source of truth keeps the web button label and the
// mobile button label in lockstep.
export const REST_XL_SEC = 60;

// The single "give me more" step of the rest row inside the effort meter
// (MX-5 slice 3b). Finer than REST_XL_SEC because that row has ONE add button,
// not a −/+ pair: the banner could afford a minute per tap with a way back,
// this can't, so it moves in the smaller grain the mockup asks for. Tapping it
// twice is still a minute.
export const REST_STEP_SEC = 30;

// Rest between two members of a superset: the time to change implement, not a
// recovery (MX-5 D9). It applies ONLY when the next set is on another member of
// the group; finishing the round takes the ordinary rest below, because that is
// where the recovery actually happens. A rhythm rather than a datum — tuned on
// Antenore at the smoke, which is why it is a named constant and not a 4 buried
// in a branch.
export const SUPERSET_REST_SEC = 4;

// Catalog-less fallback rest. 90s is the classic accessory rest;
// compound lifts seed their own value via Exercise.defaultRestSec on
// the catalog row. Also the seed value the default-picker starts from.
export const REST_FALLBACK_SEC = 90;

// Catalog-less fallback HOLD: what a timed set counts down from when the
// exercise carries no Exercise.defaultDurationSec and the profile no global
// default (and what a stopwatch→countdown flip resumes to with nothing
// remembered). Lives here beside its rest twin — same shape of decision, same
// fallback chain, so the two can't drift apart in different packages
// (next/consolidation.md, moved out of apps/mobile in MX-5 slice 3).
export const HOLD_FALLBACK_SEC = 30;

// Domain of the rest/hold default pickers (RestStepper): 5s..360s in
// 5s steps. One source of truth so the in-logger picker and the settings
// picker can't drift apart.
export const REST_PICKER_MIN = 5;
export const REST_PICKER_MAX = 360;
export const REST_PICKER_STEP = 5;

// `m:ss` — minutes are never zero-padded (90s → `1:30`, 605s → `10:05`).
// Pure, no locale yet; strength rests rarely cross 10 minutes so a fixed
// format is fine.
export function formatRestTime(sec: number): string {
  const safe = Math.max(0, Math.floor(sec));
  const m = Math.floor(safe / 60);
  const s = safe % 60;
  return `${m}:${s.toString().padStart(2, '0')}`;
}
